import * as React from 'react'
import {
    Card, TextField, Button, Box, IconButton, Grid, 
    Typography, Popover, Divider, Skeleton, Stack,
    useTheme
  } from '@mui/material'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'

import PercentageCard from './PercentageCard';
import {next_rank, prev_rank} from 'assets/utils';
import CircuitLogo from 'components/CircuitLogo';
import {Adsense} from '@ctrl/react-adsense';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import StarIcon from '@mui/icons-material/Star';
import { RankLabel } from 'components/PercentageBar';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

import { useAddFavorite, indexOf, useFavorites, useRemoveFavorite } from 'assets/favorites';
import SeasonBar from 'components/SeasonBar';
import { useSearchParams } from 'react-router-dom';
import HistoryChart from './HistoryChart';
import AdComponent from 'components/AdComponent';
import ActivityCard from './ActivityCard';
import RecentActivityCard from './RecentActivityCard';
import StatisticLine from 'components/StatisticLine';
import StatisticCircle from 'components/StatisticCircle';

import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HistoryPlayerStrengthChart from './HistoryPlayerStrengthChart';
import DisclaimerBox from 'components/DisclaimerBox';
import InfoBox from 'components/InfoBox';
import ShadowBox from 'components/ShadowBox';
import OutlinedBox from 'components/OutlinedBox';
import { useTranslation, Trans } from "react-i18next";
import PageWrapperBox from 'components/PageWrapperBox';

export default ({data, sx, canSelectSeason}) => {

    const isLoading = data === null

    const navigate = (url) => {
        window.location.href = url;
    }

    const rankStr = !isLoading && data.rank ? 'P' + data.rank : null;

    const { t } = useTranslation();

    return <>

        <PageWrapperBox sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: "row"
                }}>
                

            <Box>
            <Typography variant="body" fontSize={18} fontWeight="bold" color="primary" component="div"  sx={{cursor: "pointer", lineHeight: "1.4em"}} onClick={() => !isLoading ? navigate('https://www.tennisenpadelvlaanderen.be/nl/dashboard/resultaten?userId=' + data.tvlid) : null}>
            {isLoading ? <Skeleton width="50%"/> : data["name"]}
            </Typography>
            <Typography variant="caption" component="div" color="#777" sx={{lineHeight: "1.4em", pt: 0.25}}>
            {rankStr && <b style={{marginRight: "4px"}}>{rankStr} •</b>}{isLoading ? <Skeleton width="50%"/> : data["club"]}
            </Typography>
            </Box>
            
            {isLoading ? null: <FavoriteButton playerId={data["player_id"]} name={data["name"]} club={data["club"]}/>}

        </PageWrapperBox>

            <Box sx={{paddingTop: '30px', marginBottom: '30px', width: "100%"}}>
                <AdComponent/>
            </Box>

            <Typography variant="h6" fontWeight="bold" color="text.primary_bold" component="div" id="overview">
                {t("userCard.overview")}
            </Typography>
                
            <SeasonSelector canSelectSeason={canSelectSeason}/>

            {data ? (data?.padel ? <PredictionCardComponent data={data} canSelectSeason={canSelectSeason}/> : <NoCalculation/>) : null}

    </>
}

function NoCalculation() {
  const { t } = useTranslation();

  return <OutlinedBox sx={{py: 1, px: 1, mt: 1, fontStyle: "italic", textAlign: "center"}}>
        <Typography variant="body2" color="text.secondary">
        {t("userCard.noData")}
        </Typography>
      </OutlinedBox>
}

export function FavoriteButton({playerId, name, club, color, sx}) {

    const favorites = useFavorites();

    const [favorite, setFavorite] = React.useState(indexOf(favorites, playerId) >= 0);
    const addFavorite = useAddFavorite();
    const removeFavorite = useRemoveFavorite();

    React.useEffect(() => {
        setFavorite(indexOf(favorites, playerId) >= 0)
    }, [playerId])

    const toggleFavorite = () => {
        setFavorite(!favorite)
        if (favorite) {
            removeFavorite(playerId)
        } else {
            addFavorite({playerId: playerId, name: name, club: club})
        }
        
    }

    const theme = useTheme();

    return <IconButton display="flex" onClick={toggleFavorite} sx={{...sx}}>
        {favorite ? <StarIcon style={{color: color || theme.palette.primary.main}} fontSize='medium'/> : <StarOutlineIcon style={{color: color || theme.palette.primary.main}} fontSize='medium'/>}
    </IconButton>
}

function PredictionCardComponent({data, canSelectSeason}) {
    const theme = useTheme();
    const { t } = useTranslation();

    const current = data.padel.current_rank;
    const predicted = data.padel.predicted_rank;
    const elo = data.padel.elo_rank;
    const isFemale = data.gender == "V";

    const firstMark = prev_rank(Math.min(current, predicted), isFemale);
    const maxRank = Math.max(current, predicted);
    const lastMark = next_rank(maxRank, isFemale);

    return <Box sx={{pt: 1}}>

<PredictionCard
        rank={current}
        predRank={predicted}
        eloRank={elo}
        firstMark={firstMark}
        lastMark={lastMark}
        rankData={data["padel"]}
        isFemale={isFemale}
    />

    <InfoBox title={t("userCard.playStrengthTitle")} color={theme.palette.primary.main} titleColor={theme.palette.primary.dark} backgroundColor="#FCF9FF">
      <Trans i18nKey="userCard.playStrengthBody" components={{ b: <b style={{color: theme.palette.primary.dark}} /> }} />
    </InfoBox>

    <DisclaimerBox/>
    
    {data.history.length > 0 ? 
    <>
    <ShadowBox sx={{alignItems: "center", mt: 1, pb: 0.5}}>
        <Typography variant="body1" fontWeight="bold" sx={{borderBottom: "1px solid", borderColor: "divider", py: 1, pl: 1.5, alignSelf: "flex-start", width: "100%"}} color="text.primary_bold" component="div">
        {t("userCard.rankingsChartTitle")}
        </Typography>
    <HistoryChart history={data.history} />
    
    </ShadowBox>
    <ShadowBox sx={{alignItems: "center", mt: 1, pb: 0.5}}>
        <Typography variant="body1" fontWeight="bold" sx={{borderBottom: "1px solid", borderColor: "divider", py: 1, pl: 1.5, alignSelf: "flex-start", width: "100%"}} color="text.primary_bold" component="div">
        {t("userCard.playStrengthChartTitle")}
        </Typography>
    <HistoryPlayerStrengthChart history={data.history} />

    <HistoryPlayerStrengthBox data={data} />
    
    </ShadowBox>
    
     </> : null}

    <ShadowBox sx={{mt: 1}}>
        <Typography variant="body1" fontWeight="bold" sx={{borderBottom: "1px solid", borderColor: "divider", py: 1, pl: 1.5, alignSelf: "flex-start", width: "100%"}} color="text.primary_bold" component="div">
        {t("statistics.title")}
        </Typography>
        
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", p: 2, gap: 1}}>
            <div style={{display: "flex", alignContent: "center", justifyContent: "center", width: 160, marginBottom: "30px", marginTop: "10px"}}>
                <StatisticCircle statistic={t("statistics.matchesWon")} left={data["padel"]["nw"]} right={data["padel"]["nm"] - data["padel"]["nw"]} style={{alignSelf: "center", mt: 2, width: "160px"}}/>
            </div>

        <StatisticLine statistic={t("statistics.setsWon")} left={data["padel"]["nsw"]} right={data["padel"]["nsp"] - data["padel"]["nsw"]}/>
        <StatisticLine statistic={t("statistics.gamesWon")} left={data["padel"]["ngw"]} right={data["padel"]["ngp"] - data["padel"]["ngw"]}/>
        <StatisticLine statistic={t("statistics.tiebreaksWon")} left={data["padel"]["ntw"]} right={data["padel"]["ntp"] - data["padel"]["ntw"]}/>
        </Box>
    </ShadowBox>

    <Box sx={{paddingTop: '30px', marginBottom: '30px', width: "100%"}}>
            <AdComponent/>
    </Box>

    {data.activity && <ActivityCard activity={data["activity"]} name={data["name"]} name_club={data["club"]} rank={current}/>}
        
    {data.activity_recent?.padel_tn?.length > 0 || data.activity_recent?.padel_ic?.length > 0 ? <>
    <Typography variant="h6" fontWeight="bold" color="text.primary_bold" component="div" mt={6} mb={2}>
    {t("userCard.recentMatches")}
    </Typography>
    <RecentActivityCard activity={data["activity_recent"]} name={data["name"]} name_club={data["club"]} rank={current} />
    </> : null}

</Box>
}

function SeasonSelector({canSelectSeason}) {
    const { t } = useTranslation();

    const seasons = [
        {tag: "dec2022", label: t("seasons.winter", {year: 2023}), start: 2023},
        {tag: "jun2023", label: t("seasons.summer", {year: 2023}), start: 2023.5},
        {tag: "dec2023", label: t("seasons.winter", {year: 2024}), start: 2024},
        {tag: "jun2024", label: t("seasons.summer", {year: 2024}), start: 2024.5},
        {tag: "dec2024", label: t("seasons.winter", {year: 2025}), start: 2025},
        {tag: "jun2025", label: t("seasons.summer", {year: 2025}), start: 2025.5},
        {tag: "dec2025", label: t("seasons.winter", {year: 2026}), start: 2026},
        {tag: "jun2026", label: t("seasons.summer", {year: 2026}), start: 2026.5},
    ]
    
    const [searchParams, setSearchParams] = useSearchParams();
    const [selected, setSelected] = React.useState(seasons.length - 1);

    React.useEffect(() => {
        const s = searchParams.get("s")

        if (s) {
            var ind = seasons.length - 1;
            for (var i = 0; i < seasons.length; ++i) {
                if (seasons[i].tag == s) {
                    ind = i;
                    break
                }
            }
            setSelected(ind)
        } else {
            setSelected(seasons.length - 1)
        }
    }, [searchParams])

    const select = (i) => {
        if (canSelectSeason()) {
            const newIndex = Math.min(Math.max(i, 0), seasons.length - 1);
            setSearchParams({s: seasons[newIndex].tag})
        }
        
    }

    const marksFrom = 2023;
    const marksTo = 2027;
    
    const marks = [
        {value: 2023, label: "2023"},
        {value: 2023.5, label: null},
        {value: 2024, label: "2024"},
        {value: 2024.5, label: null},
        {value: 2025, label: "2025"},
        {value: 2025.5, label: null},
        {value: 2026, label: "2026"},
        {value: 2026.5, label: null},
    ]

    return <ShadowBox sx={{width: "100%", p: 2, pb: 0.5, mt: 2}}>

        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>

        <Button
            size="small"
            onClick={() => select(selected - 1)}
            style={{visibility: selected == 0 ? 'hidden' : 'visible'}}
          >
            <KeyboardArrowLeft /> 
          </Button>

        <Typography sx={{fontWeight: "bold", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", userSelect: 'none'}} color="primary.main">
            {seasons[selected].label}
        </Typography>

        <Button
            size="small"
            onClick={() => select(selected + 1)}
            //disabled={selected == seasons.length - 1}
            style={{visibility: selected == seasons.length - 1 ? 'hidden' : 'visible'}}
          >
            <KeyboardArrowRight />
          </Button>
    </Box>
    <SeasonBar marks={marks} from={marksFrom} to={marksTo} selectedFrom={seasons[selected].start} selectedTo={seasons[selected].start+0.5}/>
    </ShadowBox>
}

function PredictionCardRankingItem({rank, label, color, trend = null}) {
  const theme = useTheme();

  const rankStr = 'P' + rank;

  return <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", flex: "1 1 0", minWidth: 0}}>
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            align="center"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {label}
          </Typography>
                    <Typography variant="h5" component="div" color={color || theme.palette.primary.dark} align="center" sx={{fontWeight: "bold"}}>
                      {trend == "up" && <TrendingUpIcon style={{backgroundColor: theme.palette.primary.main, borderRadius: "5px", fontSize: 18, padding: "2px", marginRight: '5px', color: "white"}}/>}
                      {trend == "down" && <TrendingDownIcon style={{backgroundColor: theme.palette.primary.dark, borderRadius: "5px", fontSize: 18, padding: "2px", marginRight: '5px', color: "white"}}/>}

                      {rankStr}
                    </Typography>

                    
          
  </Box>
}

export function PredictionCard({rank, predRank, eloRank, firstMark, lastMark, rankData, isFemale}) {
  const theme = useTheme();
  const { t } = useTranslation();
  
  return <ShadowBox>
        <Typography variant="body1" component="div" fontWeight="bold" color="text.primary_bold" sx={{mt: 1.5, ml: 1.5, mb: 2}}>
            {t("prediction.title")}
        </Typography>
        <Stack sx={{flexDirection: "row", gap: 1, mb: 2, px: 1}}>
          <PredictionCardRankingItem rank={rank} label={t("prediction.current")}/>
          <Divider orientation="vertical" flexItem />
          <PredictionCardRankingItem rank={predRank} color={predRank <= rank ? theme.palette.primary.dark : theme.palette.primary.main} label={t("prediction.predicted")} trend={predRank > rank ? "up" : predRank < rank ? "down" : null}/>
          <Divider orientation="vertical" flexItem />
          <PredictionCardRankingItem rank={Math.round(eloRank / 10) * 10} color={eloRank <= rank ? theme.palette.primary.dark : theme.palette.primary.main} label={t("prediction.playStrength")}/>
        </Stack>

        <Divider sx={{mb: 1}}/>

        <Box sx={{display: "flex", justifyContent: "center"}}>
            <PercentageCard data={rankData} firstMark={firstMark} lastMark={lastMark} isFemale={isFemale}/>
        </Box>
    </ShadowBox>
}

function HistoryPlayerStrengthBox({data}) {
    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const Row = ({value1, value2, value3, delta, title}) => (
       <Stack key={value1} sx={{justifyContent: "space-between", width: "100%", flexDirection: "row", alignItems: "center", px: 0.5, py: title ? 0 : 0.5}}>
        <Typography variant="caption" sx={{fontWeight: title ? "bold" : "normal", width: "30%", textTransform: title ? "uppercase" : "default", color: title ? "#777" : "text.primary_bold"}}>
          {value1}
        </Typography>
        <Typography variant="caption" sx={{width: "40%", fontWeight: title ? "bold" : "normal", pl: 1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", textAlign: "center", textTransform: title ? "uppercase" : "default", color: title ? "#777" : "text.primary_bold"}}>
          {value2}
        </Typography>
        <Typography variant="caption" sx={{width: "30%", fontWeight: title ? "bold" : "normal", pl: 1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", textTransform: title ? "uppercase" : "default", textAlign: "right", color: title ? "#777" : "text.primary_bold", display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
          {delta ? <><b style={{marginRight: "4px", fontSize: 8, display: "inline-block", color: delta > 0 ? theme.palette.primary.light : theme.palette.primary.dark}}>{delta > 0 ? "▲" : "▼"}</b>{value3}</> : value3}
        </Typography>
      </Stack>
    )

    const recentStrength = data?.padel_strength;
    const mapDate = (str) => {
        var d = new Date(str);
        const today = new Date();

        if (d > today) {
            d = today;
        }
        return d.toLocaleDateString(i18n.language, {day: 'numeric', month: 'short', year: 'numeric'})
    }

    return recentStrength && recentStrength.length > 0 ? (
        <Box sx={{width: "calc(100% - 32px)", mx: 2, mb: 1, mt: 2}}>
          <Row title value1={t("common.date")} value2={t("speler.sections.wins")} value3={t("h2h.playStrengthShort")}/>
          <Divider sx={{width: "100%"}}/>
          {recentStrength.map((el, index) => {
              const previousStrength =
                index > 0
                  ? recentStrength[index - 1].glicko_pts
                  : null;

              const delta =
                previousStrength !== null
                  ? el.glicko_pts - previousStrength
                  : null;

              return (
                <Row
                  key={el.date}
                  value1={mapDate(el.date)}
                  value2={`${el.n_wins_delta}/${el.n_matches_delta}`}
                  value3={`P${el.glicko_pts}`}
                  delta={delta}
                />
              );
            })}
        </Box>
        ) : null;
}